import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Globe, 
  Building2, 
  MapPin, 
  Briefcase,
  Plus,
  X,
  Loader2,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// US States for dropdown
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

export default function SingleBuildPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [industryPacks, setIndustryPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: "",
    industry_key: "",
    city: "",
    state: "",
    domain: "",
    email: "",
    phone: "",
    services: [],
    service_areas: [],
    max_service_radius: 25
  });
  
  // Temp inputs for adding items
  const [newService, setNewService] = useState("");
  const [newArea, setNewArea] = useState("");

  useEffect(() => {
    fetchIndustryPacks();
  }, []);

  const fetchIndustryPacks = async () => {
    try {
      const response = await axios.get(`${API}/industry-packs`);
      setIndustryPacks(response.data);
    } catch (error) {
      console.error("Failed to fetch industry packs:", error);
      // Fallback to categories if no packs
      try {
        const catResponse = await axios.get(`${API}/categories`);
        const categories = catResponse.data.categories || [];
        setIndustryPacks(categories.map(c => ({ 
          industry_key: c.toLowerCase().replace(/\s+/g, '_'),
          display_name: c,
          default_services: [],
          default_service_areas: []
        })));
      } catch (e) {
        console.error("Failed to fetch categories:", e);
      }
    }
  };

  const handlePackChange = (packKey) => {
    const pack = industryPacks.find(p => p.industry_key === packKey);
    setSelectedPack(pack);
    setFormData(prev => ({
      ...prev,
      industry_key: packKey,
      services: pack?.default_services || [],
      service_areas: pack?.default_service_areas || [],
      max_service_radius: pack?.max_service_radius_miles || 25
    }));
  };

  const addService = () => {
    if (newService.trim() && !formData.services.includes(newService.trim())) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }));
      setNewService("");
    }
  };

  const removeService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s !== service)
    }));
  };

  const addServiceArea = () => {
    if (newArea.trim() && !formData.service_areas.includes(newArea.trim())) {
      setFormData(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, newArea.trim()]
      }));
      setNewArea("");
    }
  };

  const removeServiceArea = (area) => {
    setFormData(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter(a => a !== area)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!formData.industry_key) {
      toast.error("Please select an industry");
      return;
    }
    if (!formData.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!formData.state) {
      toast.error("State is required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/one-off-builds`, {
        ...formData,
        domain: formData.domain.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
      });
      
      toast.success("Build started! Generating preview...");
      
      // Redirect to lead detail page
      navigate(`/admin/leads/${response.data.lead_id}`);
      
    } catch (error) {
      console.error("Failed to create build:", error);
      toast.error(error.response?.data?.detail || "Failed to create build");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="single-build-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Single Site Build</h1>
          <p className="page-subtitle">
            Create a one-off website without running a campaign. No emails will be sent.
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Business Information
                </CardTitle>
                <CardDescription>
                  Enter the business details for the website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      data-testid="business-name-input"
                      placeholder="e.g., Smith's Plumbing"
                      value={formData.business_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry *</Label>
                    <Select 
                      value={formData.industry_key} 
                      onValueChange={handlePackChange}
                    >
                      <SelectTrigger data-testid="industry-select">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {industryPacks.map((pack) => (
                          <SelectItem key={pack.industry_key} value={pack.industry_key}>
                            {pack.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      data-testid="city-input"
                      placeholder="e.g., Austin"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Select 
                      value={formData.state} 
                      onValueChange={(val) => setFormData(prev => ({ ...prev, state: val }))}
                    >
                      <SelectTrigger data-testid="state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="domain">Domain (optional)</Label>
                    <Input
                      id="domain"
                      data-testid="domain-input"
                      placeholder="e.g., smithplumbing.com"
                      value={formData.domain}
                      onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email (optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      placeholder="contact@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase size={20} />
                  Services
                </CardTitle>
                <CardDescription>
                  Services offered by this business (defaults from industry pack)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {formData.services.map((service, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1 py-1">
                      {service}
                      <button
                        type="button"
                        onClick={() => removeService(service)}
                        className="ml-1 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </Badge>
                  ))}
                  {formData.services.length === 0 && (
                    <p className="text-sm text-muted-foreground">No services added. Select an industry to load defaults.</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add custom service..."
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                  />
                  <Button type="button" variant="outline" onClick={addService}>
                    <Plus size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin size={20} />
                  Service Areas
                </CardTitle>
                <CardDescription>
                  Cities/areas this business serves (generated from primary city if empty)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {formData.service_areas.map((area, i) => (
                    <Badge key={i} variant="outline" className="flex items-center gap-1 py-1">
                      {area}
                      <button
                        type="button"
                        onClick={() => removeServiceArea(area)}
                        className="ml-1 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </Badge>
                  ))}
                  {formData.service_areas.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Leave empty to auto-generate from the primary city.
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add service area..."
                    value={newArea}
                    onChange={(e) => setNewArea(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addServiceArea())}
                  />
                  <Button type="button" variant="outline" onClick={addServiceArea}>
                    <Plus size={16} />
                  </Button>
                </div>
                
                <div className="flex items-center gap-4 pt-2">
                  <Label htmlFor="radius" className="whitespace-nowrap">Max Service Radius:</Label>
                  <Input
                    id="radius"
                    type="number"
                    min="5"
                    max="100"
                    className="w-24"
                    value={formData.max_service_radius}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_service_radius: parseInt(e.target.value) || 25 }))}
                  />
                  <span className="text-sm text-muted-foreground">miles</span>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate("/admin/leads")}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                data-testid="create-build-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Creating Build...
                  </>
                ) : (
                  <>
                    <Globe size={16} className="mr-2" />
                    Create & Generate Preview
                    <ArrowRight size={16} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
